import React, {useCallback, useEffect, useState} from 'react';
import {
  Button,
  Dropdown,
  Icon,
  Label,
  DropdownItem,
  ListGroupItem,
  Toggle,
  LoadingSpinner,
  addToast
} from '@albert-io/atomic';
import {ClassroomModelV1} from '@albert-io/models';
import sessionStore from 'client/Session/SessionStore';
import classnames from 'classnames';
import {pluralize} from 'lib/stringUtils';

import './global-assignments.scss';

import {GlobalAssignmentFilterObj} from '../constants';

import {classesString} from './AssignmentCard.react';
import {getClassesQuery} from './GlobalAssignments.queries';

interface AssignmentFiltersProps {
  filters: GlobalAssignmentFilterObj;
  setFilters: (value: any) => void;
  isDraftsTab?: boolean;
  setFiltersToUse?: (value: any) => void;
  isMobile?: boolean;
}

interface ArchivedFooterProps {
  showArchived: boolean;
  setClassesPage: (page: number) => void;
  setShowArchived: (value: boolean) => void;
  classesLoading: boolean;
  toggleShowArchivedFirstTime: boolean | null;
  setToggleShowArchivedFirstTime: (value: boolean | null) => void;
}

const ArchivedFooter = ({
  showArchived,
  setClassesPage,
  setShowArchived,
  classesLoading,
  toggleShowArchivedFirstTime,
  setToggleShowArchivedFirstTime
}: ArchivedFooterProps) => {
  return (
    <>
      <span className='u-mar-r_1'>Show archived</span>
      <Toggle
        checked={showArchived}
        onChange={() => {
          setClassesPage(1);
          setShowArchived(!showArchived);
          if (toggleShowArchivedFirstTime === false) {
            setToggleShowArchivedFirstTime(true);
          }
        }}
        disabled={classesLoading}
      />
    </>
  );
};

const ClassFilter = ({
  setFilters,
  filters,
  isDraftsTab,
  setFiltersToUse,
  isMobile
}: AssignmentFiltersProps) => {
  const [classesLoading, setClassesLoading] = useState<boolean>(false);
  const [classes, setClasses] = useState<ClassroomModelV1[]>([]);
  const [classesPage, setClassesPage] = useState<number>(1);
  const [totalNumPagesClasses, setTotalNumPagesClasses] = useState<number>(0);
  const [showArchived, setShowArchived] = useState<boolean>(false);
  const [toggleShowArchivedFirstTime, setToggleShowArchivedFirstTime] = useState<boolean | null>(
    false
  );

  const onClickFilterOption = useCallback(
    (prop, option) => {
      if (prop === 'status' || prop === 'shared') {
        if (filters[prop] === option) {
          setFilters({...filters, [prop]: null});
        } else {
          setFilters({...filters, [prop]: option});
        }
      } else if (filters[prop][option.id]) {
        const newClasses = filters[prop];
        delete newClasses[option.id];
        setFilters({...filters, [prop]: newClasses});
        if (isDraftsTab && setFiltersToUse) {
          setFiltersToUse({...filters, [prop]: newClasses});
        }
      } else {
        setFilters({...filters, [prop]: {...filters[prop], [option.id]: option.name}});
        if (isDraftsTab && setFiltersToUse) {
          setFiltersToUse({
            ...filters,
            [prop]: {...filters[prop], [option.id]: option.name}
          });
        }
      }
    },
    [filters, setFilters]
  );

  const fetchClasses = useCallback(
    async ({pageNum} = {pageNum: 1}) => {
      try {
        setClassesLoading(true);
        const isAdmin = sessionStore.isSchoolAdministrator();
        let totalNumPages = 1;
        const allClassesQuery = getClassesQuery({
          includeArchived: showArchived,
          isTeacher: isDraftsTab ? true : isAdmin,
          page: pageNum
        });
        const allClasses = await allClassesQuery.getResourcePromise();
        totalNumPages = allClassesQuery.getResourceMetadata().getIn(['page', 'total_pages'], 0);
        setTotalNumPagesClasses(totalNumPages);
        if (pageNum === 1) {
          setClasses(allClasses.toArray());
        } else {
          setClasses((prevClasses) => [...prevClasses, ...allClasses.toArray()]);
        }
        setClassesLoading(false);
        if (toggleShowArchivedFirstTime === true) {
          setToggleShowArchivedFirstTime(null);
        }
      } catch (e) {
        setClassesLoading(false);
        if (toggleShowArchivedFirstTime === true) {
          setToggleShowArchivedFirstTime(null);
        }
        addToast({
          color: 'negative',
          title: "Couldn't fetch classes",
          message: 'Error fetching classes.'
        });
      }
    },
    [showArchived, isDraftsTab, toggleShowArchivedFirstTime, setToggleShowArchivedFirstTime]
  );

  const handleScrollClasses = useCallback(
    (e) => {
      if (!classesLoading) {
        const {scrollHeight, scrollTop, clientHeight} = e.target;
        const nearBottom = scrollHeight - clientHeight - scrollTop < 100;

        if (nearBottom && classesPage < totalNumPagesClasses) {
          setClassesLoading(true);
          fetchClasses({pageNum: classesPage + 1});
          setClassesPage((prevPage) => prevPage + 1);
        }
      }
    },
    [fetchClasses, classesPage, totalNumPagesClasses, classesLoading]
  );

  useEffect(() => {
    setClassesPage(1);
    fetchClasses();
  }, [fetchClasses]);

  const numClassesSelected = Object.keys(filters.classes).length;

  return (
    <>
      {!isDraftsTab && (
        <div className='u-mar-b_1 flex items-center justify-between'>
          <Label>Classes</Label>
          {!!Object.keys(filters.classes).length && (
            <button
              className='global-assignments__filters-clear content-tertiary'
              type='button'
              disabled={classesLoading}
              onClick={() =>
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  classes: {}
                }))
              }
            >
              Clear
            </button>
          )}
        </div>
      )}
      <Dropdown
        className={classnames({
          'global-assignments__filters-drafts-class': isDraftsTab
        })}
        trigger={
          <Button
            className={isDraftsTab ? '' : 'u-font-weight_normal'}
            color={isDraftsTab && numClassesSelected ? 'primary' : 'secondary'}
            variant='outlined'
            data-testid='class-trigger'
          >
            <div className='u-truncate'>
              {isDraftsTab && numClassesSelected
                ? `${numClassesSelected} ${pluralize(
                    'Class',
                    numClassesSelected,
                    'Classes'
                  )} Selected`
                : ''}
              {isDraftsTab && !numClassesSelected ? `${!isMobile ? 'Filter by' : ''} Class` : ''}
              {!isDraftsTab && classesString(filters.classes)}
            </div>
            <Icon
              icon='caret-down'
              className={classnames({
                'u-mar-l_1': isDraftsTab
              })}
            />
          </Button>
        }
        position='bottom-start'
        withMaxHeight
        trayProps={
          !isMobile && {
            modifiers: {
              flip: {
                enabled: false
              }
            }
          }
        }
      >
        <div
          className='m-dropdown__content--max u-position_relative'
          onScroll={handleScrollClasses}
        >
          <>
            {classesLoading && toggleShowArchivedFirstTime && (
              <div className='u-mar-t_2 flex justify-center'>
                <LoadingSpinner size={2} />
              </div>
            )}
            {!toggleShowArchivedFirstTime && (
              <>
                {classes.map((c) => (
                  <DropdownItem
                    bold={false}
                    key={c.id}
                    onClick={() => onClickFilterOption('classes', c)}
                    className={classnames('global-assignments__class', {
                      'global-assignments__archived-class-entry': c.status === 'archived'
                    })}
                    data-testid={`${c.id}-option`}
                    color={c.status === 'archived' ? 'inherit' : 'black'}
                  >
                    <div className='u-truncate'>
                      <Icon
                        icon='check'
                        className={classnames('u-mar-r_2 fa-fw', {
                          'u-opacity_full': filters.classes[c.id],
                          'u-opacity_0': !filters.classes[c.id]
                        })}
                      />
                      {c.name}
                    </div>
                    {c.status === 'archived' && <Icon icon='box-archive' className='u-mar-l_1' />}
                  </DropdownItem>
                ))}
              </>
            )}
            {classesLoading && !toggleShowArchivedFirstTime && (
              <div className='u-mar-t_2 flex justify-center'>
                <LoadingSpinner size={2} />
              </div>
            )}
          </>
          <ListGroupItem
            key='archived'
            className={classnames({
              'global-assignments__filters-clear-and-archived-toggle': isDraftsTab,
              'global-assignments__filters-archived-toggle': !isDraftsTab
            })}
          >
            {isDraftsTab && (
              <button
                className='global-assignments__filters-clear content-secondary'
                type='button'
                onClick={() => {
                  setFilters((prevFilters) => ({
                    ...prevFilters,
                    classes: {}
                  }));
                  if (setFiltersToUse) {
                    setFiltersToUse((prevFilters) => ({
                      ...prevFilters,
                      classes: {}
                    }));
                  }
                }}
              >
                Clear
              </button>
            )}
            {isDraftsTab ? (
              <div className='flex items-center justify-between'>
                <ArchivedFooter
                  showArchived={showArchived}
                  setClassesPage={setClassesPage}
                  setShowArchived={setShowArchived}
                  classesLoading={classesLoading}
                  toggleShowArchivedFirstTime={toggleShowArchivedFirstTime}
                  setToggleShowArchivedFirstTime={setToggleShowArchivedFirstTime}
                />
              </div>
            ) : (
              <ArchivedFooter
                showArchived={showArchived}
                setClassesPage={setClassesPage}
                setShowArchived={setShowArchived}
                classesLoading={classesLoading}
                toggleShowArchivedFirstTime={toggleShowArchivedFirstTime}
                setToggleShowArchivedFirstTime={setToggleShowArchivedFirstTime}
              />
            )}
          </ListGroupItem>
        </div>
      </Dropdown>
    </>
  );
};

export default ClassFilter;
